import moment from "moment";
import {
  contractStatuses,
  prolongDate,
  viewDocument,
} from "../../../../../helpers/data-constants";

const useStandardProlongDeal = ({
  paramReq,
  prolongCalculate,
  status,
  days,
  minDate,
  isExtendWithDaysLimit,
  isExtensionThreeImplementaition,
}) => {
  const currentDate = moment().utc().format("YYYY-MM-DD");
  const isExpiredDealStatus = (status) => status === contractStatuses.EXPIRED.param;
  const isRetentionStatus = (status) => status === contractStatuses.RETENTION.param;
  const getProlongDate = (key) => isExtensionThreeImplementaition ? prolongDate.crediton[key] : prolongDate.visem[key];

  const calculateMaxDate = (dealIndex) => {
    const firstEndDate = prolongCalculate[dealIndex]?.firstEndDate;
    return moment(firstEndDate).isSameOrBefore(moment(currentDate))
      ? moment().add(60, "days").format("YYYY-MM-DD")
      : moment(firstEndDate).add(60, "days").format("YYYY-MM-DD");
  };

  const maxExpiredDate = moment().add(60, "days").format("YYYY-MM-DD");

  const getInitialDateDefaults = (status, dealIndex = 0) => {
    const isActive = status === contractStatuses.ACTIVE.param;
    return {
      minDateDefault: isActive ? minDate : "1900-01-01",
      maxDateDefault: isActive ? calculateMaxDate(dealIndex) : maxExpiredDate,
    };
  };

  // set date to calendar in header of prolong modal
  const setHeaderDate = (isHeaderDate, dealStatus) => {
    const { maxDateDefault } = getInitialDateDefaults(dealStatus ?? status);
    const paramLength = paramReq.length;

    return isHeaderDate
      ? {
          minHeaderDate: paramLength > 1 ? undefined : minDate,
          maxHeaderDate: paramLength > 1 ? undefined : maxDateDefault,
        }
      : { minHeaderDate: null, maxHeaderDate: null };
  };

  const calculateDateDefault = ({ idx, fieldPosition }) => {
    const { minDateDefault, maxDateDefault } = getInitialDateDefaults(status, idx);

    if (isExtendWithDaysLimit) {
      return handleDaysLimit({fieldPosition});
    }

    return {
      minDateDefault,
      maxDateDefault,
      minHeaderDate: minDateDefault,
      maxHeaderDate: maxDateDefault,
    };
  };

  const getDateDefault = (status, days, minDate, maxPaidDate) => {
    let minDateDefault, maxDateDefault;
    const isExpired = isExpiredDealStatus(status);
    const isRetention = isRetentionStatus(status);
    
    if (isExtensionThreeImplementaition) {
      // Crediton standart prolong from 23.11.2024
      minDateDefault = isExpired 
        ? getInitialDateDefaults(status).minDateDefault 
        :  moment().add(1, 'days').format("YYYY-MM-DD");
      const maxDateCheck = moment(maxPaidDate).isBefore(moment(), 'days') || 
        moment().add(days, "days").isAfter(moment(maxPaidDate), 'days');
        
      maxDateDefault = maxDateCheck 
        ? moment(maxPaidDate).format("YYYY-MM-DD") 
        : moment().add(days, 'days').format("YYYY-MM-DD");
    } else {
      // Visem standart prolong from 01.10.2024
      minDateDefault = (isExpired || isRetention) 
        ?  moment().format("YYYY-MM-DD") 
        : moment(minDate).format("YYYY-MM-DD");

      maxDateDefault = (isExpired || isRetention) 
        ?  moment().add(days, 'days').format("YYYY-MM-DD") 
        : moment(minDate).subtract(1, 'days').add(days, 'days').format("YYYY-MM-DD");
    }

    return { minDateDefault, maxDateDefault };
  };

  const handleDaysLimit = ({ fieldPosition }) => {
    const paramLength = paramReq.length;
    const { status, days, maxPaidDate } = fieldPosition || {};
    const endDate = moment(fieldPosition?.endDate).add(1, "days").format("YYYY-MM-DD");

    const { minDateDefault, maxDateDefault } = getDateDefault(status, days, endDate, maxPaidDate);

    const minHeaderDate = paramLength > 1 ? undefined : minDateDefault;
    const maxHeaderDate = paramLength > 1 ? undefined : maxDateDefault;

    return {
      minDateDefault,
      maxDateDefault,
      minHeaderDate,
      maxHeaderDate,
    };
  };

  const calculateExtensionDate = (deal) => {
    if (isExtensionThreeImplementaition) {
      return isExpiredDealStatus(deal?.status ?? status)
        ? moment()
        : moment().add(deal?.days ?? days, "days");
    } else {
      return isExpiredDealStatus(deal?.status ?? status) ||
        isRetentionStatus(deal?.status ?? status)
        ? moment().add(deal?.days ?? days, "days")
        : moment(deal?.endDate).add(deal?.days ?? days, "days");
    }
  };

  const baseExtensionDate = (deal) =>
    isExtendWithDaysLimit
      ? calculateExtensionDate(deal).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");

  const isExtendBeforeDate = (startDate, deals) => {
    const isBeforeReferenceDate = moment(startDate).isBefore(prolongDate.crediton.OLD_DEAL_PROLONG_DATE);
    const hasDealBeforeReferenceDate = deals?.some((deal) => moment(deal.startDate).isBefore(prolongDate.crediton.OLD_DEAL_PROLONG_DATE));

    return (
      isExtendWithDaysLimit &&
      isExtensionThreeImplementaition &&
      (isBeforeReferenceDate || hasDealBeforeReferenceDate)
    );
  };

  const isExtendDealWithZeroDelay = (startDate, delayPercent, deals) => {
    const zeroDelayDate = getProlongDate('ZERO_DELAY_DATE');
    const newProlongDate = getProlongDate('NEW_PROLONG_DATE');
    const isDateWithinRange = (date) =>
      moment(date).isAfter(zeroDelayDate, "days") &&
      moment(date).isBefore(moment(newProlongDate).add(1, 'day'));

    const isExtendDealWithZeroDelay = startDate && isDateWithinRange(startDate) && delayPercent !== 0;
    const hasExtendDealWithZeroDelay = deals?.some(
      (deal) => isDateWithinRange(deal.startDate) && deal?.delayPercent !== 0
    );
    return (isExtendDealWithZeroDelay || hasExtendDealWithZeroDelay)
  };

  const checkGlobalProlong = (startDate, deals) => {
    const oldDealProlongDate = getProlongDate('OLD_DEAL_PROLONG_DATE');
    const checkDate = startDate && moment(startDate).isBefore(oldDealProlongDate);
    const hasProlong = deals?.some((deal) => moment(deal.startDate).isBefore(oldDealProlongDate));

    return checkDate || hasProlong;
  };

  const validateProlongDeal = ({ clientContract, deals, view }) => {
    const { status, startDate, delayPercent } = { ...clientContract };
    const isProlongView = view === viewDocument.isProlong;
    if (!isProlongView) return false;

    const hasExpiredDeals = deals?.filter((deal) => isExpiredDealStatus(deal.status)) || [];
    const hasRetentionDeals = deals?.filter((deal) => isRetentionStatus(deal.status)) || [];

    const isProlongValid =
      (isExpiredDealStatus(status) || hasExpiredDeals?.length || isRetentionStatus(status) || hasRetentionDeals?.length);

    const canExtendOldDeal = checkGlobalProlong(startDate, hasExpiredDeals.concat(hasRetentionDeals));
    
    const canExtendDealWithZeroDelay = isExtendDealWithZeroDelay(
      startDate,
      delayPercent,
      hasExpiredDeals
    );

    return isProlongValid && (canExtendOldDeal || canExtendDealWithZeroDelay);
  };

  return {
    calculateDateDefault,
    baseExtensionDate,
    validateProlongDeal,
    isExpiredDealStatus,
    isExtendBeforeDate,
  };
};

export default useStandardProlongDeal;
